import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownUp, Loader2, Check, Share2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { getRates, API } from "@/lib/api";

// Currencies available to send from
const FROM_OPTS = [
  { code: "USD", flag: "🇺🇸", symbol: "$" },
  { code: "GBP", flag: "🇬🇧", symbol: "£" },
  { code: "EUR", flag: "🇪🇺", symbol: "€" },
  { code: "CAD", flag: "🇨🇦", symbol: "C$" },
  { code: "AUD", flag: "🇦🇺", symbol: "A$" },
  { code: "CHF", flag: "🇨🇭", symbol: "Fr" },
];

// Country → default send currency
const COUNTRY_TO_CCY = {
  GB: "GBP", IE: "EUR", FR: "EUR", DE: "EUR", NL: "EUR", ES: "EUR", IT: "EUR",
  BE: "EUR", PT: "EUR", AT: "EUR", FI: "EUR", GR: "EUR", LU: "EUR",
  US: "USD", CA: "CAD", AU: "AUD", NZ: "AUD", CH: "CHF",
};

// Currencies available to receive
const TO_OPTS = [
  { code: "NGN", flag: "🇳🇬", symbol: "₦", short: "Nigeria" },
  { code: "KES", flag: "🇰🇪", symbol: "KSh", short: "Kenya" },
  { code: "GHS", flag: "🇬🇭", symbol: "GH₵", short: "Ghana" },
  { code: "XOF", flag: "🇸🇳", symbol: "CFA", short: "Senegal" },
  { code: "ZAR", flag: "🇿🇦", symbol: "R", short: "S. Africa" },
  { code: "UGX", flag: "🇺🇬", symbol: "USh", short: "Uganda" },
  { code: "MAD", flag: "🇲🇦", symbol: "MAD", short: "Morocco" },
  { code: "EGP", flag: "🇪🇬", symbol: "E£", short: "Egypt" },
  { code: "TZS", flag: "🇹🇿", symbol: "TSh", short: "Tanzania" },
];

const FEE_RATE = 0.009;

export default function SendWidget() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [amount, setAmount] = useState(500);
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("NGN");
  const [rates, setRates] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [userTouched, setUserTouched] = useState(false);

  useEffect(() => {
    getRates().then((d) => setRates(d.pairs || [])).catch(() => {});
  }, []);

  // Corridor Auto-Detect on mount — sets default send currency from IP country
  useEffect(() => {
    if (userTouched) return;
    let ignore = false;
    const detect = async () => {
      // Try our backend first (uses CF header or X-Forwarded-For)
      try {
        const r = await fetch(`${API}/geo`);
        const j = await r.json();
        if (!ignore && j?.country && COUNTRY_TO_CCY[j.country]) {
          setFrom(COUNTRY_TO_CCY[j.country]);
          return;
        }
      } catch (_) { /* try fallback */ }
      // Fallback: public geo API (no auth)
      try {
        const r = await fetch("https://api.country.is/");
        const j = await r.json();
        if (!ignore && j?.country && COUNTRY_TO_CCY[j.country]) {
          setFrom(COUNTRY_TO_CCY[j.country]);
        }
      } catch (_) { /* silent */ }
    };
    detect();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const touch = (setter) => (v) => {
    setUserTouched(true);
    setter(v);
  };

  // Rate resolver with cross-rate fallback
  const rate = useMemo(() => {
    const direct = rates.find((p) => p.from === from && p.to === to);
    if (direct) return direct.rate;
    const usdTo = rates.find((p) => p.from === "USD" && p.to === to);
    if (from === "USD" && usdTo) return usdTo.rate;
    if (usdTo) {
      const usdPerFrom = { USD: 1, GBP: 1.27, EUR: 1.09, CAD: 0.73, AUD: 0.66, CHF: 1.14 }[from] || 1;
      return usdTo.rate * usdPerFrom;
    }
    return 1;
  }, [rates, from, to]);

  const fromMeta = FROM_OPTS.find((x) => x.code === from) || FROM_OPTS[0];
  const toMeta = TO_OPTS.find((x) => x.code === to) || TO_OPTS[0];
  const fee = +(amount * FEE_RATE).toFixed(2);
  const netSend = amount - fee;
  const receive = netSend * rate;

  const formatMoney = (n, decimals = 2) =>
    n.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

  // Send → waitlist handoff with pre-fill context
  const triggerSend = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);
      setTimeout(() => {
        nav(
          `/waitlist?send=${encodeURIComponent(amount)}` +
          `&from=${from}&to=${to}&receive=${Math.round(receive)}`
        );
      }, 1100);
    }, 1300);
  };

  // Share this quote (crawler-friendly URL)
  const shareUrl = `${API}/quote-share?amount=${amount}&from_ccy=${from}&to_ccy=${to}&receive=${Math.round(receive)}`;

  const shareQuote = async () => {
    const text = `${fromMeta.symbol}${formatMoney(amount, 0)} ${from} → ${toMeta.symbol}${formatMoney(receive, 0)} ${to} · Instantxfer CashApp · 0.9% flat, ~2 min.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "My Instantxfer quote", text, url: shareUrl });
        return;
      } catch (_) { /* dismissed */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* ignore */ }
  };

  return (
    <section className="py-24 md:py-32 bg-surface relative" data-testid="send-widget-section">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* LEFT — editorial copy */}
        <div className="lg:col-span-5">
          <div className="label-eyebrow">{t("home.send.eyebrow")}</div>
          <h2 className="mt-4 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
            {t("home.send.title")} <span className="italic font-serif text-forest/80">{t("home.send.italic")}</span>
          </h2>
          <p className="mt-6 max-w-md text-ink/70 leading-relaxed">
            This is the real math — 0.9% flat, mid-market rate, no spread. Pick a corridor, punch in an amount, watch what actually lands in their pocket.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            <MiniKpi kpi="0.9%" label="Flat fee" />
            <MiniKpi kpi="0" label="FX spread" />
            <MiniKpi kpi="~2 min" label="Settlement" />
          </div>
        </div>

        {/* RIGHT — the widget */}
        <div className="lg:col-span-7 flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className="w-full max-w-[520px] rounded-[32px] bg-forest text-canvas relative overflow-hidden shadow-[0_50px_120px_-40px_rgba(45,13,94,0.6)]"
            data-testid="send-widget"
          >
            {/* Ambient yellow glow */}
            <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full bg-lime/15 blur-3xl" />
            <div className="pointer-events-none absolute -left-24 bottom-0 w-72 h-72 rounded-full bg-forest-light/25 blur-3xl" />

            <div className="relative p-6 md:p-8">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-lime">
                  Send · Live quote
                </div>
                <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-canvas/60">
                  <span className="live-dot" /> mid-market
                </span>
              </div>

              {/* You send */}
              <div className="mt-6 rounded-2xl bg-white/[0.04] border border-canvas/12 p-5">
                <div className="flex items-center justify-between text-[10px] tracking-[0.22em] uppercase text-canvas/60">
                  <span>You send</span>
                  <span>from</span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-4">
                  <div className="flex items-baseline gap-1.5 min-w-0">
                    <span className="text-canvas/50 text-2xl md:text-3xl">{fromMeta.symbol}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min={1}
                      value={amount}
                      onChange={(e) => { setUserTouched(true); setAmount(Math.max(0, Number(e.target.value) || 0)); }}
                      className="bg-transparent font-display text-4xl md:text-5xl tracking-tightest text-canvas outline-none w-full min-w-0"
                      data-testid="send-widget-amount"
                    />
                  </div>
                  <CurrencyPicker
                    value={from}
                    onChange={touch(setFrom)}
                    options={FROM_OPTS}
                    testId="send-widget-from"
                  />
                </div>
              </div>

              {/* Divider with swap */}
              <div className="my-4 flex items-center gap-3">
                <div className="flex-1 h-px bg-canvas/12" />
                <div className="w-9 h-9 rounded-full bg-lime text-ink grid place-items-center shrink-0">
                  <ArrowDownUp className="w-4 h-4" strokeWidth={2.2} />
                </div>
                <div className="flex-1 h-px bg-canvas/12" />
              </div>

              {/* They receive */}
              <div className="rounded-2xl bg-lime/95 text-ink p-5 border border-lime">
                <div className="flex items-center justify-between text-[10px] tracking-[0.22em] uppercase text-ink/60 font-semibold">
                  <span>They receive</span>
                  <span>{toMeta.short}</span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-4">
                  <div className="flex items-baseline gap-1.5 min-w-0">
                    <span className="text-ink/60 text-2xl md:text-3xl">{toMeta.symbol}</span>
                    <span
                      className="font-display text-4xl md:text-5xl tracking-tightest text-ink truncate"
                      data-testid="send-widget-receive"
                    >
                      {formatMoney(receive, receive >= 1000 ? 0 : 2)}
                    </span>
                  </div>
                  <CurrencyPicker
                    value={to}
                    onChange={touch(setTo)}
                    options={TO_OPTS}
                    testId="send-widget-to"
                    invert
                  />
                </div>
              </div>

              {/* Breakdown */}
              <dl className="mt-5 space-y-2 text-sm">
                <Row
                  k="Rate"
                  v={`1 ${from} = ${formatMoney(rate, rate >= 100 ? 2 : 4)} ${to}`}
                  testId="send-widget-rate"
                />
                <Row
                  k="Fee (0.9% flat)"
                  v={`${fromMeta.symbol}${formatMoney(fee)}`}
                  testId="send-widget-fee"
                />
                <Row k="Delivery" v="~ 2 minutes" muted />
              </dl>

              {/* Send button */}
              <div className="mt-6 flex items-stretch gap-2">
                <button
                  type="button"
                  onClick={triggerSend}
                  disabled={sending || amount <= 0}
                  data-testid="send-widget-cta"
                  className={`flex-1 rounded-full py-4 font-display text-lg tracking-tight flex items-center justify-center gap-2 transition-all ${
                    sent
                      ? "bg-white text-forest"
                      : "bg-lime text-ink hover:brightness-110 active:scale-[0.98]"
                  } ${sending || amount <= 0 ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Locking mid-market rate…
                    </>
                  ) : sent ? (
                    <>
                      <Check className="w-5 h-5" /> Would settle in ~2 minutes
                    </>
                  ) : (
                    <>
                      Send {fromMeta.symbol}
                      {formatMoney(amount, 0)} now <ArrowUpRight className="w-5 h-5" strokeWidth={2.4} />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={shareQuote}
                  disabled={sending}
                  aria-label="Share this quote"
                  data-testid="send-widget-share"
                  className="shrink-0 w-14 rounded-full bg-white/[0.06] border border-canvas/20 text-canvas hover:bg-lime hover:text-ink hover:border-lime transition-all grid place-items-center"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" strokeWidth={2.2} />}
                </button>
              </div>

              <div className="mt-4 text-center">
                <Link
                  to="/waitlist"
                  className="text-[11px] tracking-[0.22em] uppercase text-canvas/60 hover:text-lime transition-colors"
                  data-testid="send-widget-waitlist-link"
                >
                  Reserve a real seat →
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Row({ k, v, muted, testId }) {
  return (
    <div className="flex items-center justify-between">
      <dt className={`text-[11px] tracking-[0.2em] uppercase ${muted ? "text-canvas/40" : "text-canvas/55"}`}>
        {k}
      </dt>
      <dd className="font-mono text-canvas text-sm" data-testid={testId}>
        {v}
      </dd>
    </div>
  );
}

function CurrencyPicker({ value, onChange, options, testId, invert }) {
  return (
    <label
      className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-full border cursor-pointer ${
        invert
          ? "border-ink/20 bg-ink/5 text-ink"
          : "border-canvas/20 bg-white/[0.04] text-canvas"
      }`}
    >
      <span className="text-lg leading-none">
        {options.find((o) => o.code === value)?.flag}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent font-semibold text-sm outline-none appearance-none pr-1"
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.code} value={o.code} className="text-ink bg-white">
            {o.code}
          </option>
        ))}
      </select>
    </label>
  );
}

function MiniKpi({ kpi, label }) {
  return (
    <div>
      <div className="font-display text-2xl md:text-3xl tracking-tightest text-forest">{kpi}</div>
      <div className="mt-1 text-[10px] tracking-[0.22em] uppercase text-ink/50">{label}</div>
    </div>
  );
}
